import React, { useEffect } from 'react';
import 'twin.macro';
import Card from 'components/Card';
import { BoWithLocalFallbackCycle } from 'components/Bo/BoWithLocalFallbackCycle';
import { RankThisItemButton } from 'containers/CarnetsDeNotes/Evaluations/RankThisItemButton';
import { Field, useField } from 'react-final-form';
import { Eleve } from 'types/eleves';
import { useTrackSuggestions } from 'components/Bo/useBoSuggestionTracking';
import {
  Referential,
  Note,
} from 'containers/CarnetsDeNotes/types/carnetsDeNotes';
import { Infobox } from 'components/Callout/Infobox';
import FadeIn from 'components/Transitions/FadeIn';
import { CopyEvalButton } from 'containers/CarnetsDeNotes/Evaluations/CopyEvalButton';
import { useTranslation } from 'react-i18next';
import { useDeleteNotesWarningModal } from './DeleteNotesWarningModal/useDeleteNotesWarningModal';
import { Item } from '@teetsh/app/src/components/Form/AutoSuggestGroup/types';
import { NotationSystem } from '@teetsh/app/src/types/notationSystem';

interface Props {
  boId: string;
  eleves: Eleve[];
  evaluationId: string;
  isGuest?: boolean;
  carnetDeNotesId: string;
  notationSystem?: NotationSystem;
}

function findDeletedItems(
  oldItems: Item[] | undefined,
  newItems: Item[] | undefined,
): Item[] {
  if (!oldItems || !newItems) return [];
  return oldItems.filter(
    oldItem => !newItems.some(newItem => newItem.id === oldItem.id),
  );
}

function hasNotesForReferentialItem(
  notes: Note[],
  referentialItemId: string,
): boolean {
  return notes.some(
    note => note.referentialItemId === referentialItemId && note.note != null,
  );
}

export function BoField(props: Props) {
  const nameField = useField('name');
  const { t } = useTranslation(['carnet', 'common']);
  const referentialField = useField<Referential>('referential');
  const notesField = useField<Note[]>('notes');
  const trackSuggestions = useTrackSuggestions();
  const { open: openWarningModal } = useDeleteNotesWarningModal();
  const { boId, eleves, evaluationId, notationSystem } = props;
  useEffect(
    () => () => {
      const referential = referentialField.input.value;
      trackSuggestions({
        page: 'Evaluations',
        title: nameField.input.value,
        matieres: (referential.matieres || []).map(m => m.value),
        domaines: (referential.domaines || []).map(d => d.value),
        competences: (referential.competences || []).map(c => c.value),
        goals: (referential.objectifs || []).map(g => g.value),
      });
    },
    [],
  );
  return (
    <Field<Referential>
      name="referential"
      render={({ input: { value, onChange } }) => (
        <Card tw="overflow-visible">
          <BoWithLocalFallbackCycle
            boId={boId}
            suggestionParams={{
              title: nameField.input.value,
            }}
            suggestionEntity="evals"
            values={{
              competence: value?.competences,
              cycle: value?.cycle,
              domaine: value?.domaines,
              goals: value?.objectifs || [],
              matiere: value?.matieres,
            }}
            onChange={({ competence, cycle, domaine, goals, matiere }) => {
              const currentReferential = value;
              const notes = notesField.input.value;

              // Check for deleted items in each category
              const deletedCompetences = findDeletedItems(
                currentReferential?.competences,
                competence,
              );
              const deletedDomaines = findDeletedItems(
                currentReferential?.domaines,
                domaine,
              );
              const deletedMatieres = findDeletedItems(
                currentReferential?.matieres,
                matiere,
              );
              const deletedObjectifs = findDeletedItems(
                currentReferential?.objectifs,
                goals,
              );

              // Combine all deleted items
              const allDeletedItems = [
                ...deletedCompetences,
                ...deletedDomaines,
                ...deletedMatieres,
                ...deletedObjectifs,
              ];

              // Check if any deleted item has associated notes
              const hasNotesToDelete = allDeletedItems.some(item =>
                hasNotesForReferentialItem(notes, item.id),
              );

              const updateReferential = () => {
                // Update the referential
                onChange({
                  competences: competence,
                  cycle,
                  domaines: domaine,
                  objectifs: goals,
                  matieres: matiere,
                });

                // If there are deleted items, also remove their associated notes
                if (allDeletedItems.length > 0) {
                  const deletedItemIds = allDeletedItems.map(item => item.id);
                  const filteredNotes = notes.filter(
                    note =>
                      !deletedItemIds.includes(note.referentialItemId || ''),
                  );
                  notesField.input.onChange(filteredNotes);
                }
              };

              if (hasNotesToDelete) {
                openWarningModal({
                  callback: updateReferential,
                });
              } else {
                updateReferential();
              }
            }}
          >
            <RankThisItemButton eleves={eleves} evaluationId={evaluationId} notationSystem={notationSystem} />
          </BoWithLocalFallbackCycle>
          {['Cycle 2', 'Cycle 3'].includes(value.cycle) &&
            ((value?.matieres || []).length > 1 ||
              (value?.domaines || []).length > 1) && (
              <FadeIn>
                <Infobox
                  title={t('carnet:evaluationForm.warningMultiMatieresTitle')}
                  intent="warning"
                  tw="mt-6"
                >
                  <p>{t('carnet:evaluationForm.warningMultiMatieresPart1')}</p>
                  <br />
                  {t('carnet:evaluationForm.warningMultiMatieresPart2')}
                  <br />
                  {!props.isGuest && (
                    <CopyEvalButton
                      tw="pt-4"
                      evaluationId={evaluationId}
                      carnetDeNotesId={props.carnetDeNotesId}
                    >
                      {t('carnet:actions.duplicateEvaluation')}
                    </CopyEvalButton>
                  )}
                </Infobox>
              </FadeIn>
            )}
        </Card>
      )}
    />
  );
}
